/**
 * The spender's own order of the wallets/houses (Arrange on Home). Kept per user on the
 * device; entities missing from the saved order keep their original position at the end.
 */
/**
 * `list` names what is being ordered: the default is the wallets/houses; category tiles use
 * categoryList(entityId), since every house has its own categories.
 */
const key = (userId: string, list: string) => `expense.${list}.v1.${userId}`;

export const categoryList = (entityId: string) => `categoryOrder.${entityId}`;

export function loadOrder(storage: Storage | undefined, userId: string, list = 'entityOrder'): string[] {
  try {
    const raw = storage?.getItem(key(userId, list));
    const ids: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function saveOrder(storage: Storage | undefined, userId: string, ids: string[], list = 'entityOrder'): void {
  try {
    if (ids.length) storage?.setItem(key(userId, list), JSON.stringify(ids));
    else storage?.removeItem(key(userId, list));
  } catch {
    /* storage blocked: the order still applies for this visit */
  }
}

/** Entities sorted by the saved order; unknown ones keep their original order, after. */
export function applyOrder<T extends { id: string }>(items: T[], order: string[]): T[] {
  const rank = new Map(order.map((id, i) => [id, i]));
  return items
    .map((item, i) => ({ item, i }))
    .sort((a, b) => (rank.get(a.item.id) ?? order.length + a.i) - (rank.get(b.item.id) ?? order.length + b.i))
    .map(({ item }) => item);
}

/** Takes `id` out and puts it at position `to` (drag and drop); others shift to make room. */
export function moveTo(ids: string[], id: string, to: number): string[] {
  const from = ids.indexOf(id);
  if (from < 0) return ids;
  const target = Math.max(0, Math.min(to, ids.length - 1));
  if (target === from) return ids;
  const next = ids.filter((x) => x !== id);
  next.splice(target, 0, id);
  return next;
}

/** Moves one id earlier (-1) or later (+1); does nothing at the ends. */
export function move(ids: string[], id: string, dir: -1 | 1): string[] {
  const i = ids.indexOf(id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= ids.length) return ids;
  const next = [...ids];
  [next[i], next[j]] = [next[j] as string, next[i] as string];
  return next;
}
